// Consent-aware Firebase Analytics loader.
//
// DSGVO/TTDSG compliance pattern:
// - Nothing is loaded or executed until the user grants Statistik-consent.
// - When consent is granted, Firebase + Analytics are dynamically imported,
//   so the SDK does not weigh on the initial page bundle either.
// - When consent is revoked, collection is disabled and GA cookies are
//   cleared from the current document so Google receives no further hits.
import { firebaseConfig } from './firebase-config.js';

let analyticsInstance = null;
let firebaseApp = null;
let loadingPromise = null;
let cachedLogEvent = null;
let queuedEvents = [];
let consentExplicitlyDenied = false;

export async function enableAnalytics() {
  consentExplicitlyDenied = false;

  // Re-enabling after a previous disable: analyticsInstance is still set but
  // we wiped cachedLogEvent. Re-import logEvent + re-enable collection + drain
  // any events that were queued in the meantime, then return.
  if (analyticsInstance) {
    if (!cachedLogEvent) {
      try {
        const { logEvent, setAnalyticsCollectionEnabled } = await import('firebase/analytics');
        cachedLogEvent = logEvent;
        setAnalyticsCollectionEnabled(analyticsInstance, true);
        if (queuedEvents.length) {
          const drain = queuedEvents.splice(0);
          for (const [name, params] of drain) {
            try { logEvent(analyticsInstance, name, params); } catch (e) { /* noop */ }
          }
        }
      } catch (e) { /* noop */ }
    }
    return analyticsInstance;
  }
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const [{ initializeApp, getApps, getApp }, { getAnalytics, isSupported, setAnalyticsCollectionEnabled, logEvent }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      loadingPromise = null;
      return null;
    }

    firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
    analyticsInstance = getAnalytics(firebaseApp);
    setAnalyticsCollectionEnabled(analyticsInstance, true);
    cachedLogEvent = logEvent;

    // Drain any events queued before init
    if (queuedEvents.length) {
      const drain = queuedEvents.splice(0);
      for (const [name, params] of drain) {
        try { logEvent(analyticsInstance, name, params); } catch (e) { /* noop */ }
      }
    }
    return analyticsInstance;
  })();

  return loadingPromise;
}

export async function disableAnalytics() {
  consentExplicitlyDenied = true;
  if (analyticsInstance) {
    try {
      const { setAnalyticsCollectionEnabled } = await import('firebase/analytics');
      setAnalyticsCollectionEnabled(analyticsInstance, false);
    } catch {
      /* noop */
    }
  }
  cachedLogEvent = null;
  queuedEvents = [];
  clearGaCookies();
}

// Fire a GA4 event. Safe to call before consent — events queue and drain on enableAnalytics().
// If consent is never granted, the queue is GC'd at page unload — nothing leaves the browser.
export function logGAEvent(eventName, params = {}) {
  if (analyticsInstance && cachedLogEvent) {
    try { cachedLogEvent(analyticsInstance, eventName, params); } catch (e) { /* noop */ }
    return;
  }
  // Drop on the floor if consent was explicitly denied
  if (consentExplicitlyDenied) return;
  // Otherwise queue (consent may not have been decided yet); cap queue to avoid unbounded growth
  if (queuedEvents.length < 50) queuedEvents.push([eventName, params]);
}

export function isAnalyticsEnabled() {
  return !!analyticsInstance;
}

function clearGaCookies() {
  if (typeof document === 'undefined') return;
  const measurementId = firebaseConfig.measurementId || '';
  const gaSuffix = measurementId.replace(/^G-/, '');
  const names = ['_ga', '_gid', '_gat', `_ga_${gaSuffix}`, `_gac_${gaSuffix}`];
  const host = window.location.hostname;
  // Cookies may be scoped to the apex domain — clear common variants.
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}
